import { useCallback, useEffect, useMemo, useState } from 'react';
import type { CoreOrganization } from '@/lib/zifekCore';
import {
  CUSTOM_FIELD_ENTITIES,
  CUSTOM_FIELD_TYPES,
  RELATION_TARGET_ENTITIES,
  createCustomField,
  customFieldEntityMeta,
  customFieldTypeMeta,
  deleteCustomField,
  fetchCustomFieldUsage,
  listCustomFields,
  updateCustomField,
  type CoreCustomField,
  type CustomFieldType,
} from '@/lib/coreCustomFields';
import Modal from '@/pages/dashboard/core/components/Modal';

interface Props {
  commerceId: number;
  org: CoreOrganization;
}

const INPUT = 'w-full px-3 py-2 rounded-md border border-background-200/70 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-colors';

interface FForm {
  id: number | null;
  entityType: string;
  label: string;
  fieldType: CustomFieldType;
  optionsText: string;
  relationEntity: string;
  required: boolean;
}
const EMPTY: FForm = { id: null, entityType: 'customer', label: '', fieldType: 'text', optionsText: '', relationEntity: '', required: false };

export default function CustomFieldsTab({ org }: Props) {
  const [fields, setFields] = useState<CoreCustomField[]>([]);
  const [usage, setUsage] = useState<Record<number, number>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [entity, setEntity] = useState('customer');
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<FForm>(EMPTY);
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [list, u] = await Promise.all([listCustomFields(org.id), fetchCustomFieldUsage(org.id)]);
      setFields(list);
      setUsage(u);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Chargement impossible.');
    } finally {
      setLoading(false);
    }
  }, [org.id]);

  useEffect(() => { load(); }, [load]);

  const list = useMemo(() => fields.filter((f) => f.entity_type === entity), [fields, entity]);

  const openCreate = () => { setForm({ ...EMPTY, entityType: entity }); setOpen(true); };
  const openEdit = (f: CoreCustomField) => {
    const opts = Array.isArray(f.options) ? (f.options as string[]) : [];
    setForm({
      id: f.id,
      entityType: f.entity_type,
      label: f.label,
      fieldType: f.field_type as CustomFieldType,
      optionsText: opts.join(', '),
      relationEntity: f.relation_entity || '',
      required: !!f.required,
    });
    setOpen(true);
  };

  const save = async () => {
    if (!form.label.trim()) return;
    setSaving(true);
    try {
      const options = form.optionsText.split(',').map((o) => o.trim()).filter(Boolean);
      if (form.id) {
        await updateCustomField(form.id, {
          label: form.label.trim(),
          fieldType: form.fieldType,
          options,
          relationEntity: form.fieldType === 'relation' ? form.relationEntity : null,
          required: form.required,
        });
      } else {
        await createCustomField({
          tenantId: org.tenant_id,
          organizationId: org.id,
          entityType: form.entityType,
          label: form.label.trim(),
          fieldType: form.fieldType,
          options,
          relationEntity: form.fieldType === 'relation' ? form.relationEntity : null,
          required: form.required,
        });
      }
      setOpen(false);
      load();
    } finally {
      setSaving(false);
    }
  };

  const remove = async (f: CoreCustomField) => {
    setBusyId(f.id);
    try { await deleteCustomField(f.id); load(); } finally { setBusyId(null); }
  };

  const toggleActive = async (f: CoreCustomField) => {
    setBusyId(f.id);
    try { await updateCustomField(f.id, { isActive: !f.is_active }); load(); } finally { setBusyId(null); }
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <p className="text-xs text-foreground-500 max-w-2xl">
          Champs personnalisés : ajoutez vos propres champs (« Numéro de dossier interne », « Type de projet »…) sans modifier la structure de la base.
          <span className="font-semibold text-foreground-700"> Entity → Définition → Valeur</span>.
        </p>
        <button onClick={openCreate} className="flex items-center gap-2 px-4 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer">
          <i className="ri-add-line"></i> Nouveau champ
        </button>
      </div>

      <div className="flex flex-wrap items-center gap-1 bg-background-100 rounded-full p-1 w-fit">
        {CUSTOM_FIELD_ENTITIES.map((e) => (
          <button key={e.key} onClick={() => setEntity(e.key)} className={`flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors cursor-pointer ${entity === e.key ? 'bg-background-50 text-foreground-950 shadow-sm' : 'text-foreground-500 hover:text-foreground-700'}`}>
            <i className={e.icon}></i>{e.label}
          </button>
        ))}
      </div>

      {error && <div className="p-3 bg-red-50 border border-red-200 rounded-md text-xs text-red-700">{error}</div>}

      {loading ? (
        <div className="flex items-center justify-center py-16"><i className="ri-loader-4-line animate-spin text-2xl text-primary-500"></i></div>
      ) : list.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 bg-background-50 border border-background-200/70 rounded-lg">
          <div className="w-14 h-14 rounded-full bg-background-100 flex items-center justify-center mb-3"><i className="ri-input-field text-2xl text-foreground-400"></i></div>
          <p className="text-foreground-600">Aucun champ personnalisé pour « {customFieldEntityMeta(entity)?.label} ».</p>
        </div>
      ) : (
        <div className="space-y-2">
          {list.map((f) => {
            const t = customFieldTypeMeta(f.field_type);
            const opts = Array.isArray(f.options) ? (f.options as string[]) : [];
            return (
              <div key={f.id} className="flex items-center gap-3 bg-background-50 border border-background-200/70 rounded-lg p-3">
                <span className="w-9 h-9 rounded-lg bg-background-100 flex items-center justify-center flex-shrink-0"><i className={`${t.icon} text-foreground-600`}></i></span>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm font-semibold text-foreground-900">{f.label}</span>
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-secondary-100 text-secondary-800 whitespace-nowrap">{t.label}</span>
                    {f.required && <span className="text-[10px] px-2 py-0.5 rounded-full bg-accent-100 text-accent-900 whitespace-nowrap">Requis</span>}
                    {!f.is_active && <span className="text-[10px] px-2 py-0.5 rounded-full bg-background-100 text-foreground-500 whitespace-nowrap">Inactif</span>}
                  </div>
                  <p className="text-[11px] text-foreground-400 mt-1">
                    clé : {f.field_key}
                    {opts.length > 0 ? ` · options : ${opts.join(', ')}` : ''}
                    {f.relation_entity ? ` · → ${f.relation_entity}` : ''}
                    {` · ${usage[f.id] || 0} valeur(s)`}
                  </p>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  <button onClick={() => toggleActive(f)} disabled={busyId === f.id} title={f.is_active ? 'Désactiver' : 'Activer'} className="w-8 h-8 flex items-center justify-center rounded-full text-foreground-500 hover:bg-background-100 transition-colors cursor-pointer disabled:opacity-50"><i className={f.is_active ? 'ri-toggle-fill text-primary-600' : 'ri-toggle-line'}></i></button>
                  <button onClick={() => openEdit(f)} className="w-8 h-8 flex items-center justify-center rounded-full text-foreground-500 hover:bg-background-100 transition-colors cursor-pointer"><i className="ri-edit-line"></i></button>
                  <button onClick={() => remove(f)} disabled={busyId === f.id} className="w-8 h-8 flex items-center justify-center rounded-full text-foreground-400 hover:text-red-600 hover:bg-red-50 transition-colors cursor-pointer disabled:opacity-50"><i className="ri-delete-bin-line"></i></button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {open && (
        <Modal
          title={form.id ? 'Modifier le champ' : 'Nouveau champ personnalisé'}
          onClose={() => setOpen(false)}
          maxWidth="max-w-lg"
          footer={
            <>
              <button onClick={() => setOpen(false)} className="px-4 py-2 rounded-full text-sm font-medium text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap">Annuler</button>
              <button onClick={save} disabled={saving || !form.label.trim()} className="px-4 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer disabled:opacity-50 flex items-center gap-2">
                {saving ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-check-line"></i>} Enregistrer
              </button>
            </>
          }
        >
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <label className="block">
                <span className="block text-xs font-medium text-foreground-600 mb-1">Entité</span>
                <select className={INPUT} value={form.entityType} onChange={(e) => setForm({ ...form, entityType: e.target.value })} disabled={!!form.id}>
                  {CUSTOM_FIELD_ENTITIES.map((e) => <option key={e.key} value={e.key}>{e.label}</option>)}
                </select>
              </label>
              <label className="block">
                <span className="block text-xs font-medium text-foreground-600 mb-1">Type de champ</span>
                <select className={INPUT} value={form.fieldType} onChange={(e) => setForm({ ...form, fieldType: e.target.value as CustomFieldType })}>
                  {CUSTOM_FIELD_TYPES.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
                </select>
              </label>
            </div>
            <label className="block">
              <span className="block text-xs font-medium text-foreground-600 mb-1">Libellé</span>
              <input className={INPUT} value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} placeholder="Ex : Numéro de dossier interne" />
            </label>
            {(form.fieldType === 'select' || form.fieldType === 'multi-select') && (
              <label className="block">
                <span className="block text-xs font-medium text-foreground-600 mb-1">Options (séparées par des virgules)</span>
                <input className={INPUT} value={form.optionsText} onChange={(e) => setForm({ ...form, optionsText: e.target.value })} placeholder="Ex : Petit, Moyen, Grand" />
              </label>
            )}
            {form.fieldType === 'relation' && (
              <label className="block">
                <span className="block text-xs font-medium text-foreground-600 mb-1">Entité cible de la relation</span>
                <select className={INPUT} value={form.relationEntity} onChange={(e) => setForm({ ...form, relationEntity: e.target.value })}>
                  <option value="">— Choisir —</option>
                  {RELATION_TARGET_ENTITIES.map((r) => <option key={r} value={r}>{r}</option>)}
                </select>
              </label>
            )}
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={form.required} onChange={(e) => setForm({ ...form, required: e.target.checked })} className="w-4 h-4 rounded border-background-300 text-primary-500 focus:ring-primary-300" />
              <span className="text-sm text-foreground-700">Champ obligatoire</span>
            </label>
          </div>
        </Modal>
      )}
    </div>
  );
}